'use client'
import { useState, FormEvent, Suspense } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { inputClassComfortable, primaryButtonClassBlock } from '@/lib/ui-styles'

// Two logins, one screen. `platform` is read from AUTH_MODE on the SERVER (in
// ./page.tsx) and passed in as a prop — deliberately not via
// NEXT_PUBLIC_AUTH_MODE, which Next inlines at build time and would turn a
// rollback into an image rebuild.
//
// The difference is not cosmetic: platform-auth authenticates by EMAIL, while
// 811's own login authenticates by USERNAME. Same screen, different identifier,
// different endpoint. This is the one user-visible change of the whole migration,
// and the operators are told about it on paper before their tenant is flipped.
function LoginForm({ platform }: { platform: boolean }) {
  const router = useRouter()
  const searchParams = useSearchParams()
  const isSafeRedirect = (p: string) => p.startsWith('/') && !p.startsWith('//')
  const dest = isSafeRedirect(searchParams.get('redirect') ?? '') ? searchParams.get('redirect')! : '/'

  const [identifier, setIdentifier] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError('')
    setLoading(true)

    try {
      const res = await fetch(platform ? '/api/auth/platform/login' : '/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(
          platform ? { email: identifier, password } : { username: identifier, password },
        ),
      })

      if (!res.ok) {
        const data = await res.json().catch(() => ({}))
        setError(data.error ?? 'Login failed')
        return
      }

      router.push(dest)
      router.refresh()
    } catch {
      setError('Network error — please try again')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-sm bg-white rounded-xl shadow-sm border border-gray-100 p-8">

        {/* Branding */}
        <div className="mb-8 text-center">
          <div className="text-xs font-semibold uppercase tracking-widest text-brand-orange mb-1">
            HRGroup
          </div>
          <h1 className="text-xl font-bold text-brand-navy">811 Ticket Management</h1>
          <p className="text-sm text-gray-500 mt-1">Sign in to your account</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">
              {platform ? 'Email' : 'Username'}
            </label>
            <input
              type={platform ? 'email' : 'text'}
              autoComplete={platform ? 'email' : 'username'}
              value={identifier}
              onChange={e => setIdentifier(e.target.value)}
              required
              className={inputClassComfortable}
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1.5">
              Password
            </label>
            <input
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              className={inputClassComfortable}
            />
          </div>

          {error && (
            <p className="text-sm text-red-600">{error}</p>
          )}

          <button
            type="submit"
            disabled={loading}
            className={`${primaryButtonClassBlock} mt-2`}
          >
            {loading ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
      </div>
    </div>
  )
}

export default function LoginFormBoundary({ platform }: { platform: boolean }) {
  return (
    <Suspense>
      <LoginForm platform={platform} />
    </Suspense>
  )
}
